/**
 * The page registry — the SOURCE OF TRUTH for what is routable, what is gated,
 * and what is drawn in the navbar.
 *
 * `src/routes.tsx` GENERATES the route table from this file, deliberately, so a
 * route cannot exist without a permission check. `lib/rbac/route-guard.ts`
 * gates by looking a pathname up here. `backend/api/app/rbac.py` holds a MIRRORED
 * copy for the API side; the two must agree on which pages exist, or a page
 * renders and then 403s (newly denied) or is gated by a key nobody checks
 * (newly reachable).
 *
 * ─── V5-33 navigation restructure (2026-08-21) ──────────────────────────────
 *
 * The navbar is now:
 *
 *     DASHBOARD · RECORDS · RENT · LOANS · INVEST · ITR      + Admin (avatar)
 *
 * superseding the previous locked set (dashboard / analytics / accounts /
 * loans / itr / rent / records). Admin left the bar entirely — it lives in the
 * avatar menu, and it was never a registry key on either stack anyway
 * (`isAdminOnlyPath` gates `/admin*` and `/settings*` by ROLE, before the
 * registry is ever consulted).
 *
 * TWO RULES GOVERNED THE RESTRUCTURE, and both are load-bearing:
 *
 *  1. **No page key was renamed, added-as-a-replacement, or removed.** Grant
 *     rows in `PagePermission` are keyed by `pageKey`. Renaming `accounts` to,
 *     say, `records-accounts` would leave every existing grant row pointing at
 *     a key this build no longer knows — `normalizePermissionRows` drops
 *     unknown keys, a dropped key is a missing row, and a missing row is a
 *     DENY. A family member would silently lose a page. So the restructure is
 *     expressed ENTIRELY through `hideFromNav` + `parent`, which are display
 *     concerns, never through the `key` or the `matchPrefix`, which are the
 *     access contract. The twelve pre-existing rows below are byte-identical in
 *     key, href, matchPrefix and adminOnly to what they were before.
 *
 *  2. **Leaving the navbar is not leaving the app.** A page that folds into
 *     another keeps its route, its guard and its permission-matrix row; it is
 *     flagged `hideFromNav` and given a `parent`, which is what the section
 *     nav (`components/layout/SectionNav.tsx`) and the host page's tabs use to
 *     keep it reachable. Deleting an entry would leave the route with NO
 *     permission check at all — the exact trap this comment has always warned
 *     about.
 *
 * `invest` is the ONE new key. Being new, no `PagePermission` row exists for it,
 * and a missing row is a deny for role USER — so no user gains access to
 * anything by its addition. Admins see it because ADMIN short-circuits every
 * lookup, which is true of every page. It becomes grantable through the normal
 * matrix editor (`admin_permissions.VALID_KEYS` derives from the registry).
 *
 * Adding an eighth nav entry is still forbidden: a new feature becomes a TAB or
 * a `parent`-ed sub-page inside one of the six, not a new destination in the
 * bar.
 */
import {
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  Home,
  FileText,
  PiggyBank,
  BarChart3,
  Building2,
  Wallet,
  Landmark,
  ListOrdered,
  LineChart,
  CandlestickChart,
  Vault,
  BookmarkPlus,
  type LucideIcon,
} from "lucide-react";

export type PageKey =
  | "dashboard"
  | "analytics"
  | "income"
  | "expenses"
  | "accounts"
  | "loans"
  | "home-loan"
  | "itr"
  | "budget"
  | "yearly-summary"
  | "rent"
  | "records"
  | "invest"
  | "vault"
  | "templates";

export type PageAction = "view" | "edit" | "delete";

export interface PageDef {
  key: PageKey;
  label: string;
  href: string;
  icon: LucideIcon;
  matchPrefix: string;
  /** Hard admin-only module (backend enforces role ADMIN) — excluded from the
   *  per-user permission grant matrix so a USER can never be granted a page
   *  whose API would 403 them anyway. */
  adminOnly?: boolean;
  /**
   * The page keeps its route, its guard and its permission-matrix row, but is
   * not drawn as a top-level nav entry — it is reached from inside another
   * page instead (a tab, a section-nav chip, a tile drill-through).
   *
   * Registry membership is what gates a route: the guard calls `findPageByPath`
   * and denies when `resolvePermission` says no. Deleting an entry would
   * therefore leave the route with NO permission check at all, so a page that
   * leaves the nav is flagged here, never removed.
   */
  hideFromNav?: boolean;
  /**
   * Which nav section this page folds into. Only meaningful for `hideFromNav`
   * pages; it is a DISPLAY relationship (which section chip highlights, which
   * strip lists it) and grants nothing — the page is still gated by its own
   * `key`, independently of its parent's.
   */
  parent?: PageKey;
}

export const PAGE_REGISTRY: PageDef[] = [
  { key: "dashboard",      label: "Dashboard",      href: "/dashboard",      icon: LayoutDashboard, matchPrefix: "/dashboard" },
  { key: "analytics",      label: "Analytics",      href: "/analytics",      icon: LineChart,       matchPrefix: "/analytics",      hideFromNav: true, parent: "dashboard" },
  { key: "income",         label: "Income",         href: "/income",         icon: TrendingUp,      matchPrefix: "/income",         hideFromNav: true, parent: "records" },
  { key: "expenses",       label: "Expenses",       href: "/expenses",       icon: TrendingDown,    matchPrefix: "/expenses",       hideFromNav: true, parent: "records" },
  { key: "accounts",       label: "Accounts",       href: "/accounts",       icon: Wallet,          matchPrefix: "/accounts",       hideFromNav: true, parent: "dashboard" },
  { key: "loans",          label: "Loans",          href: "/loans",          icon: Home,            matchPrefix: "/loans" },
  { key: "home-loan",      label: "Loans",          href: "/home-loan",      icon: Landmark,        matchPrefix: "/home-loan",      adminOnly: true, hideFromNav: true, parent: "loans" },
  { key: "itr",            label: "ITR / Tax",      href: "/itr",            icon: FileText,        matchPrefix: "/itr" },
  { key: "budget",         label: "Budget",         href: "/budget",         icon: PiggyBank,       matchPrefix: "/budget",         hideFromNav: true, parent: "records" },
  { key: "yearly-summary", label: "Yearly Summary", href: "/yearly-summary", icon: BarChart3,       matchPrefix: "/yearly-summary", hideFromNav: true, parent: "itr" },
  { key: "rent",           label: "Rent",           href: "/rent",           icon: Building2,       matchPrefix: "/rent" },
  { key: "records",        label: "Records",        href: "/records",        icon: ListOrdered,     matchPrefix: "/records" },
  { key: "invest",         label: "Invest",         href: "/invest",         icon: CandlestickChart, matchPrefix: "/invest" },
  // v5.0 family document vault + warranty tracker, registered here on its
  // owning agent's behalf. Folded under Records, not a seventh nav entry.
  // NOTE: unlike every other key, `vault` does NOT default to denied — its
  // migration 0022 backfills an OWN-scope grant for every existing user, so
  // nobody is locked out of their own passport. That is a deliberate access
  // GRANT owned by that change, not a side effect of this restructure.
  { key: "vault",          label: "Vault",          href: "/vault",          icon: Vault,           matchPrefix: "/vault",          hideFromNav: true, parent: "records" },
  // V5-35 -- named, reusable entry templates, registered here on their
  // owning agent's behalf (same arrangement as `vault` above). Folded under
  // Records, not a seventh nav entry.
  //
  // Like `invest` and unlike `vault`, this is a NEW key with no backfill, so
  // a missing PagePermission row is a DENY and nobody gains access by its
  // addition -- admins see it immediately, everyone else once an admin grants
  // it through the normal matrix editor.
  //
  // The GATE IS ON THE PAGE, NOT ON THE FEATURE: `/py-api/v1/templates` is
  // authenticated-only with no page-permission check, exactly like
  // `/suggestions` and `/preferences`, so the <TemplatePicker/> inside the
  // entry forms works for every user regardless of this grant. What the grant
  // controls is the standalone manage SCREEN.
  { key: "templates",      label: "Templates",      href: "/templates",      icon: BookmarkPlus,    matchPrefix: "/templates",      hideFromNav: true, parent: "records" },
];

/**
 * The navbar, in the order the user asked for:
 *
 *     DASHBOARD · RECORDS · RENT · LOANS · INVEST · ITR
 *
 * Declared explicitly rather than derived from `PAGE_REGISTRY`'s own order,
 * because the two orders are answering different questions. The registry's
 * order is historical (append-only, so a key never moves under a grant row);
 * the bar's order is editorial. Deriving one from the other would force a
 * registry reshuffle every time the IA changes, which is exactly the kind of
 * churn that loses a `PagePermission` row.
 *
 * `NAV_PAGES` and the `hideFromNav` flags are kept consistent by
 * `tests/nav-registry.test.ts`, which fails if a page is a nav entry in one
 * and not the other.
 */
export const NAV_ORDER: PageKey[] = [
  "dashboard",
  "records",
  "rent",
  "loans",
  "invest",
  "itr",
];

const BY_KEY = new Map<PageKey, PageDef>(PAGE_REGISTRY.map((p) => [p.key, p]));

export function findPageByKey(key: PageKey): PageDef | undefined {
  return BY_KEY.get(key);
}

/**
 * The six top-level destinations. Everything else in `PAGE_REGISTRY` is still
 * routed, still gated and still grantable; it is just reached from inside its
 * `parent`:
 *
 *   analytics / accounts    → Dashboard
 *   income / expenses / budget → Records
 *   home-loan               → Loans (admins only)
 *   yearly-summary          → ITR
 */
export const NAV_PAGES: PageDef[] = NAV_ORDER.map((key) => {
  const page = BY_KEY.get(key);
  if (!page) throw new Error(`NAV_ORDER names an unknown page key: ${key}`);
  return page;
});

/**
 * The pages that fold into `key`, in registry order — what the section nav
 * strip draws under the top bar, and what a host page can offer as tabs.
 *
 * The section's own page is FIRST, so a caller can render the whole strip from
 * one array. Callers must still filter by `useCanViewPage`: this function
 * answers "what belongs here", never "what may this user see".
 */
export function sectionPages(key: PageKey): PageDef[] {
  const self = BY_KEY.get(key);
  const children = PAGE_REGISTRY.filter((p) => p.parent === key);
  return self ? [self, ...children] : children;
}

/** The nav section a pathname belongs to — its page's `parent`, or itself. */
export function sectionForPath(pathname: string): PageKey | undefined {
  const page = findPageByPath(pathname);
  if (!page) return undefined;
  return page.parent ?? page.key;
}

export function findPageByPath(pathname: string): PageDef | undefined {
  return PAGE_REGISTRY.find(
    (p) => pathname === p.matchPrefix || pathname.startsWith(p.matchPrefix + "/"),
  );
}
